/**
 * DOM joystick. At rest it sits bottom-centre; while a thumb is down it moves
 * to wherever that thumb landed, and returns to centre on release.
 *
 * Why it floats: at arcade pace players reported the stick covering the very
 * part of the road they were steering along. A stick that appears under the
 * thumb lives near a bottom corner instead of dead centre, so the road stays
 * visible — and it works the same for either hand, since there is no fixed
 * side to reach across to.
 *
 * Product requirement 6 ("joystick centred horizontally at the bottom") still
 * holds where it matters: the resting position is unchanged and still centred,
 * which is what a player sees before they choose a hand, and what the layout
 * acceptance test measures. Only the in-contact position follows the thumb.
 *
 * `?stick=fixed` restores the old always-centred behaviour for an A/B.
 *
 * Pointer events (touch on mobile Safari/Chrome, mouse in desktop dev).
 * Produces raw JoystickCommand values; the sim applies the deadzone and
 * control latency.
 */

import type { JoystickCommand } from "../sim/types";

export type StickLayout = "floating" | "fixed";

/** Keep the whole base on screen when it jumps to the thumb (px). */
const EDGE_MARGIN = 8;

export class Joystick {
  private cmd: JoystickCommand = { dirX: 0, dirY: 0, mag: 0 };
  private readonly zone: HTMLElement;
  private readonly base: HTMLElement;
  private readonly thumb: HTMLElement;
  private readonly layout: StickLayout;
  private activePointer: number | null = null;
  /** Base centre while a thumb is down; null means "resting at centre". */
  private origin: { x: number; y: number } | null = null;

  constructor(zone: HTMLElement, layout: StickLayout = "floating") {
    this.zone = zone;
    this.layout = layout;
    zone.innerHTML = `
      <div class="joystick-base" id="joystick">
        <div class="joystick-thumb" id="joystick-thumb"></div>
      </div>`;
    this.base = zone.querySelector("#joystick") as HTMLElement;
    this.thumb = zone.querySelector("#joystick-thumb") as HTMLElement;
    if (layout === "floating") zone.classList.add("floating");

    // A floating stick has to hear the touch that starts it, which can land
    // anywhere in the band rather than on the base itself.
    const target: HTMLElement = layout === "floating" ? zone : this.base;
    target.addEventListener("pointerdown", this.onDown);
    target.addEventListener("pointermove", this.onMove);
    target.addEventListener("pointerup", this.onUp);
    target.addEventListener("pointercancel", this.onUp);
    target.addEventListener("lostpointercapture", this.onUp);
  }

  /**
   * Stop the band swallowing touches — used while the end-of-attempt panel is
   * up, since the band overlaps it and would otherwise eat its buttons.
   */
  setEnabled(enabled: boolean): void {
    this.zone.classList.toggle("disabled", !enabled);
    if (!enabled) this.release();
  }

  /** Current raw command (read once per sim step). */
  read(): JoystickCommand {
    return this.cmd;
  }

  setVisible(visible: boolean): void {
    this.base.style.display = visible ? "" : "none";
  }

  private onDown = (e: PointerEvent): void => {
    if (this.activePointer !== null) return;
    this.activePointer = e.pointerId;
    if (this.layout === "floating") this.moveBaseTo(e.clientX, e.clientY);
    (this.layout === "floating" ? this.zone : this.base).setPointerCapture(e.pointerId);
    this.base.classList.add("active");
    this.update(e);
  };

  /** Put the base under the thumb, kept fully on screen. */
  private moveBaseTo(x: number, y: number): void {
    const r = this.base.getBoundingClientRect();
    const half = r.width / 2;
    const cx = Math.min(Math.max(x, half + EDGE_MARGIN), window.innerWidth - half - EDGE_MARGIN);
    const cy = Math.min(Math.max(y, half + EDGE_MARGIN), window.innerHeight - half - EDGE_MARGIN);
    const zr = this.zone.getBoundingClientRect();
    this.origin = { x: cx, y: cy };
    this.base.style.left = `${cx - zr.left - half}px`;
    this.base.style.top = `${cy - zr.top - half}px`;
  }

  private onMove = (e: PointerEvent): void => {
    if (e.pointerId !== this.activePointer) return;
    this.update(e);
  };

  private onUp = (e: PointerEvent): void => {
    if (e.pointerId !== this.activePointer) return;
    this.release();
  };

  /** Back to neutral, and back to the centre of the band. */
  private release(): void {
    this.activePointer = null;
    this.origin = null;
    this.base.classList.remove("active");
    this.cmd = { dirX: 0, dirY: 0, mag: 0 }; // release = yield
    this.thumb.style.transform = "translate(-50%, -50%)";
    if (this.layout === "floating") {
      this.base.style.left = "";
      this.base.style.top = "";
    }
  }

  private update(e: PointerEvent): void {
    const rect = this.base.getBoundingClientRect();
    const R = rect.width / 2;
    // Measure from where the stick actually is, which while floating is the
    // point the thumb started from, not the middle of the band.
    const originX = this.origin ? this.origin.x : rect.left + R;
    const originY = this.origin ? this.origin.y : rect.top + R;
    const dx = e.clientX - originX;
    const dy = e.clientY - originY;
    const dist = Math.hypot(dx, dy);
    if (dist < 1e-3) {
      this.cmd = { dirX: 0, dirY: 0, mag: 0 };
    } else {
      // Screen y grows downward; forward (up) is +dirY.
      this.cmd = {
        dirX: dx / dist,
        dirY: -dy / dist,
        mag: Math.min(1, dist / R),
      };
    }
    const clamped = Math.min(dist, R);
    const tx = dist > 0 ? (dx / dist) * clamped : 0;
    const ty = dist > 0 ? (dy / dist) * clamped : 0;
    this.thumb.style.transform = `translate(calc(-50% + ${tx}px), calc(-50% + ${ty}px))`;
  }
}
